import { lazy, useState } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { BrowserRouter, Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { FournisseurAuth, RequiertAuth } from '../lib/auth';
import { ErreurApi } from '../lib/api';
import { usePermissions } from '../lib/permissions';
import { revoquerSessionAppels } from '../lib/session-appels';
import { FournisseurIcones, FournisseurToasts } from '../composants/ui';
import { AccesReserve, AlerteAccesRefuse, RequiertPermission } from '../composants/Autorisation';
import { Differe } from './Differe';
import { Layout } from './Layout';
import { PERMISSIONS_PARAMETRES, SECTIONS_PARAMETRES } from './navigation';
import type { AdresseSection } from './navigation';
import { PageConnexion } from '../pages/Connexion';
import { PageMotDePasseImpose } from '../pages/MotDePasse';

/*
 * Les ecrans authentifies sont charges a la demande : recharts (tableau de
 * bord) et dnd-kit (pipeline) representent l'essentiel du poids applicatif et
 * ne sont pas necessaires pour afficher la page de connexion.
 */
const PageTableauDeBord = lazy(async () => ({
  default: (await import('../pages/TableauDeBord')).PageTableauDeBord,
}));
const PageAujourdhui = lazy(async () => ({
  default: (await import('../pages/Aujourdhui')).PageAujourdhui,
}));
const PageRelances = lazy(async () => ({
  default: (await import('../pages/Relances')).PageRelances,
}));
const PageSessionAppels = lazy(async () => ({
  default: (await import('../pages/SessionAppels')).PageSessionAppels,
}));
const PageListeLeads = lazy(async () => ({
  default: (await import('../pages/ListeLeads')).PageListeLeads,
}));
const PageFicheLead = lazy(async () => ({
  default: (await import('../pages/FicheLead')).PageFicheLead,
}));
const PagePipeline = lazy(async () => ({
  default: (await import('../pages/Pipeline')).PagePipeline,
}));
const PageTrouverLeads = lazy(async () => ({
  default: (await import('../pages/TrouverLeads')).PageTrouverLeads,
}));
const PageProfil = lazy(async () => ({ default: (await import('../pages/Profil')).PageProfil }));
const PageParametres = lazy(async () => ({
  default: (await import('../pages/parametres/Parametres')).PageParametres,
}));
const PageParametresEquipe = lazy(async () => ({
  default: (await import('../pages/parametres/Equipe')).PageParametresEquipe,
}));
const PageParametresOffre = lazy(async () => ({
  default: (await import('../pages/parametres/OffreIa')).PageParametresOffre,
}));
const PageParametresPipeline = lazy(async () => ({
  default: (await import('../pages/parametres/Pipeline')).PageParametresPipeline,
}));
const PageParametresIntegrations = lazy(async () => ({
  default: (await import('../pages/parametres/Integrations')).PageParametresIntegrations,
}));
const PageIntrouvable = lazy(async () => ({
  default: (await import('../pages/Introuvable')).PageIntrouvable,
}));

/** Ecran de chaque section : une section sans ecran, ou un ecran sans section, ne compile pas. */
const ECRANS_PARAMETRES = {
  '/parametres/equipe': PageParametresEquipe,
  '/parametres/offre': PageParametresOffre,
  '/parametres/pipeline': PageParametresPipeline,
  '/parametres/integrations': PageParametresIntegrations,
} satisfies Record<AdresseSection, unknown>;

function creerClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        // Outil de travail consulte en continu : on evite les rechargements
        // a chaque retour d'onglet tout en gardant les donnees fraiches.
        staleTime: 30_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        retry: (nbEchecs, erreur) => {
          // Inutile de reessayer une erreur d'authentification ou de saisie.
          if (erreur instanceof ErreurApi && erreur.statut < 500) return false;
          return nbEchecs < 2;
        },
      },
      mutations: { retry: false },
    },
  });
}

/**
 * Redirection d'une ancienne adresse. La query string suit : un favori vers
 * l'ancien Kanban filtre ouvre le pipeline avec les memes filtres.
 */
export function Redirection({ vers }: { vers: string }) {
  const { search, hash } = useLocation();
  return <Navigate to={{ pathname: vers, search, hash }} replace />;
}

/**
 * `/parametres` ouvre la premiere section permise : aucune n'est commune a
 * tous les roles qui voient les parametres.
 */
function RedirectionParametres() {
  const { peut } = usePermissions();
  const section = SECTIONS_PARAMETRES.find((candidate) => peut(candidate.permission));
  return section ? <Navigate to={section.vers} replace /> : <AccesReserve />;
}

export function RoutesApplication() {
  return (
    <Routes>
      <Route path="/connexion" element={<PageConnexion />} />
      <Route
        path="/mot-de-passe"
        element={
          <RequiertAuth pourChangementMotDePasse>
            <PageMotDePasseImpose />
          </RequiertAuth>
        }
      />
      <Route
        element={
          <RequiertAuth>
            <Layout />
          </RequiertAuth>
        }
      >
        <Route path="/" element={<Differe><PageAujourdhui /></Differe>} />
        <Route path="/relances" element={<Differe><PageRelances /></Differe>} />
        <Route
          path="/session"
          element={
            <RequiertPermission permission="leads.ecrire">
              <Differe><PageSessionAppels /></Differe>
            </RequiertPermission>
          }
        />
        <Route path="/statistiques" element={<Differe><PageTableauDeBord /></Differe>} />
        <Route path="/leads" element={<Differe><PageListeLeads /></Differe>} />
        <Route path="/leads/:id" element={<Differe><PageFicheLead /></Differe>} />
        <Route path="/pipeline" element={<Differe><PagePipeline /></Differe>} />
        <Route
          path="/trouver"
          element={
            <RequiertPermission permission="leads.importer">
              <Differe><PageTrouverLeads /></Differe>
            </RequiertPermission>
          }
        />
        <Route path="/profil" element={<Differe><PageProfil /></Differe>} />
        <Route
          path="/parametres"
          element={
            <RequiertPermission permission={PERMISSIONS_PARAMETRES}>
              <Differe><PageParametres /></Differe>
            </RequiertPermission>
          }
        >
          <Route index element={<RedirectionParametres />} />
          {/* Gardee par la permission qui affiche la section dans la navigation. */}
          {SECTIONS_PARAMETRES.map(({ vers, permission }) => {
            const Ecran = ECRANS_PARAMETRES[vers];
            return (
              <Route
                key={vers}
                path={vers}
                element={
                  <RequiertPermission permission={permission}>
                    <Differe><Ecran /></Differe>
                  </RequiertPermission>
                }
              />
            );
          })}
          <Route path="*" element={<RedirectionParametres />} />
        </Route>
        <Route path="/kanban" element={<Redirection vers="/pipeline" />} />
        <Route path="/import" element={<Redirection vers="/trouver" />} />
        <Route path="/jetons" element={<Redirection vers="/parametres/integrations" />} />
        <Route path="*" element={<Differe><PageIntrouvable /></Differe>} />
      </Route>
    </Routes>
  );
}

/**
 * Ce que le compte quitte : le cache de l'API et la session d'appels, qui garde
 * dans l'onglet les noms, numeros et notes de ses leads. Appelee a chaque
 * fin de session : deconnexion, 401, ou un autre compte ouvert dans un autre
 * onglet (`FournisseurAuth`). Revoquee plutot qu'effacee : une ecriture que
 * l'ecran de session avait en attente ne la retablit pas.
 */
export function purgerSession(client: QueryClient): void {
  client.clear();
  revoquerSessionAppels();
}

export function App() {
  const [client] = useState(creerClient);

  // Les icones englobent les toasts : leur portail, rendu par
  // FournisseurToasts, herite ainsi de la taille commune. Les toasts englobent
  // l'authentification, qui peut donc en emettre.
  return (
    <QueryClientProvider client={client}>
      <BrowserRouter>
        <FournisseurIcones>
          <FournisseurToasts>
            <FournisseurAuth onDeconnexion={() => purgerSession(client)}>
              <AlerteAccesRefuse />
              <RoutesApplication />
            </FournisseurAuth>
          </FournisseurToasts>
        </FournisseurIcones>
      </BrowserRouter>
    </QueryClientProvider>
  );
}
